// Server functions for the personal Palworld card collection binder.
// Owner reads/writes run through requireSupabaseAuth (RLS as the member).
// Public reads go through a secret, expiring, revocable share token, which the
// database helper validates and audits — no account needed to view a shared
// collection.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  COLLECTION_CAPACITY,
  addCardSchema,
  moveCardSchema,
  removeCardSchema,
  revokeShareSchema,
  shareLabelSchema,
  shareTokenSchema,
  updateCardSchema,
  type CollectionRow,
} from "./collection-shared";

const COLUMNS =
  "id, card_code, slot, quantity, condition, buy_price, sell_price, sold_price, sold_at, notes, created_at";


/** Every card the signed-in member tracks, in pocket order. */
export const getMyCollection = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("card_collection")
      .select(COLUMNS)
      .eq("user_id", context.userId)
      .order("slot", { ascending: true, nullsFirst: false })
      .limit(500);
    if (error) throw error;
    return (data ?? []) as CollectionRow[];
  });

async function firstFreeSlot(
  supabase: { from: (t: string) => any },
  userId: string,
  wanted: number | null,
): Promise<number | null> {
  const { data, error } = await supabase
    .from("card_collection")
    .select("slot")
    .eq("user_id", userId)
    .not("slot", "is", null);
  if (error) throw error;
  const taken = new Set<number>((data ?? []).map((r: { slot: number }) => r.slot));
  if (wanted !== null && !taken.has(wanted)) return wanted;
  for (let i = 0; i < COLLECTION_CAPACITY; i++) if (!taken.has(i)) return i;
  return null;
}

/** File a card from the Pal Stars card database into the collection binder. */
export const addCollectionCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => addCardSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const slot = await firstFreeSlot(supabase, userId, data.slot);
    if (slot === null) throw new Error("Your collection binder is full — free a pocket first.");
    const { data: row, error } = await supabase
      .from("card_collection")
      .insert({
        user_id: userId,
        card_code: data.card_code.toUpperCase(),
        quantity: data.quantity,
        condition: data.condition,
        buy_price: data.buy_price,
        sell_price: data.sell_price,
        notes: data.notes,
        slot,
      })
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return row as CollectionRow;
  });

/** Edit buy price, sell price, quantity, condition or notes. */
export const updateCollectionCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateCardSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { id, ...patch } = data;
    const { data: row, error } = await context.supabase
      .from("card_collection")
      .update(patch)
      .eq("id", id)
      .eq("user_id", context.userId)
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return row as CollectionRow;
  });

/** Drag a tracked card into an exact pocket, swapping with any occupant. */
export const moveCollectionCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => moveCardSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: moving, error: movingErr } = await supabase
      .from("card_collection")
      .select("id, slot")
      .eq("id", data.id)
      .eq("user_id", userId)
      .maybeSingle();
    if (movingErr) throw movingErr;
    if (!moving) throw new Error("That card is not in your collection.");
    if (moving.slot === data.slot) return { ok: true, swapped: false };

    const { data: occupant } = await supabase
      .from("card_collection")
      .select("id")
      .eq("user_id", userId)
      .eq("slot", data.slot)
      .maybeSingle();

    if (occupant) {
      const { error: parkErr } = await supabase
        .from("card_collection")
        .update({ slot: null })
        .eq("id", occupant.id)
        .eq("user_id", userId);
      if (parkErr) throw parkErr;
    }

    const { error: moveErr } = await supabase
      .from("card_collection")
      .update({ slot: data.slot })
      .eq("id", data.id)
      .eq("user_id", userId);
    if (moveErr) throw moveErr;

    if (occupant) {
      const fallback = moving.slot ?? (await firstFreeSlot(supabase, userId, null));
      const { error: swapErr } = await supabase
        .from("card_collection")
        .update({ slot: fallback })
        .eq("id", occupant.id)
        .eq("user_id", userId);
      if (swapErr) throw swapErr;
    }
    return { ok: true, swapped: Boolean(occupant) };
  });

export const removeCollectionCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => removeCardSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("card_collection")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw error;
    return { ok: true };
  });

/** Mint a secret, 30-day, revocable public share link for the collection. */
export const createCollectionShareLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => shareLabelSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { data: token, error } = await context.supabase.rpc("create_collection_share_token", {
      _label: data.label,
    });
    if (error) throw error;
    return { token: token as string };
  });

export const listCollectionShareLinks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("collection_share_tokens")
      .select("id, token, label, revoked, expires_at, views, created_at")
      .order("created_at", { ascending: false })
      .limit(25);
    if (error) throw error;
    return data ?? [];
  });

export const revokeCollectionShareLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => revokeShareSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("collection_share_tokens")
      .update({ revoked: true })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

/**
 * Public tokenized collection read: no account required. The database helper
 * enforces that the token exists, is unrevoked and unexpired, counts the view
 * and writes the read audit entry. It is service-role only, so this server
 * function is the single entry point and only ever forwards a validated token.
 */
export const getCollectionByToken = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => shareTokenSchema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("get_collection_by_token", {
      _token: data.token,
    });
    if (error) throw new Error("This share link is no longer active.");
    const list = (rows ?? []) as Array<CollectionRow & { owner_name: string }>;
    return {
      owner: list[0]?.owner_name ?? "Trainer",
      cards: list.map(({ owner_name: _o, ...card }): CollectionRow => card),
    };
  });
